import { gql } from '@apollo/client';
import { SuspenseContext } from '@nerdwallet/apollo/suspense-context';
import { useContext, useState } from 'react';

export type PressRelease = {
  title: string;
  date: string;
  url: string;
  mediaSites: { name: string }[];
};

type PressReleasesData = {
  pressReleases: {
    items: PressRelease[];
    hasNextPage: boolean;
    currentOffset: number;
    total: number;
  };
};

export const PRESS_RELEASE_ARCHIVE_GQL_QUERY = gql`
  query pressReleases($offset: Int, $limit: Int) {
    pressReleases(
      pagination: { limit: $limit, offset: $offset }
      order: { field: DATE, sortDirection: DESC }
    ) {
      hasNextPage
      total
      currentOffset
      items {
        url
        title
        date
        mediaSites {
          name
        }
      }
    }
  }
`;

const usePressReleasesArchive = (offset?: number, limit?: number) => {
  const { maybeUseSuspenseQuery } = useContext(SuspenseContext);
  const { data, error, fetchMore } = maybeUseSuspenseQuery(
    PRESS_RELEASE_ARCHIVE_GQL_QUERY,
    {
      variables: {
        offset,
        limit,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    }
  ) as { data: PressReleasesData; error: any; fetchMore: any };

  const [isLoading, setIsLoading] = useState(false);
  const current = data?.pressReleases?.currentOffset || 0;

  const handleUpdateQuery = (
    prev: PressReleasesData,
    { fetchMoreResult }: { fetchMoreResult: PressReleasesData }
  ) => {
    const element = document.getElementById('_archive-browser');
    element?.focus();
    setIsLoading(false);
    if (!fetchMoreResult) return prev;
    return fetchMoreResult;
  };
  const handleSetNextSlide = () => {
    if (isLoading) return;

    setIsLoading(true);
    fetchMore({
      variables: {
        offset: current + (limit || 10),
        limit,
      },
      updateQuery: handleUpdateQuery,
    });
  };

  const handlePrevSlide = () => {
    if (isLoading) return;

    setIsLoading(true);
    fetchMore({
      variables: {
        offset: current - (limit || 10),
        limit,
      },
      updateQuery: handleUpdateQuery,
    });
  };

  if (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[usePressReleasesArchive] Error fetching press releases:',
      error
    );
  }

  const articles = data?.pressReleases?.items || [];
  const hasMore = data?.pressReleases?.hasNextPage;

  return {
    articles,
    current,
    hasMore,
    handleSetNextSlide,
    handlePrevSlide,
    isLoading,
    error,
  };
};

export default usePressReleasesArchive;
